import { computed, ref, watch, type Ref } from 'virtual-vue';
import type { InputResultOptions, KeyboardInputProps } from './keyboard-input';

/**
 * 处理自定义输入内容和光标位置
 * @param props 组件props
 * @param emit 事件触发
 * @returns 返回包含输入状态和操作方法的对象
 */
export function useKeyboardInput(props: KeyboardInputProps, emit: any) {
  // 当前光标位置
  const cursorPos: Ref<number> = ref(0);
  const values: Ref<string[]> = ref([]);
  const chars = computed(() => ['|', ...values.value]);

  /**  初始化value默认值，同时监听value值变化 */
  function initData() {
    if (props.value) {
      values.value = props.value.split('');
      setCursorPos();
    }
    watch(
      () => props.value,
      (newVal) => {
        const oldVal = values.value.join('');
        if (newVal !== oldVal) {
          values.value = (newVal || '').split('');
          setCursorPos();
        }
      },
    );
  }

  /**
   * 内容输入通知事件
   * @param isDelete 是否是删除事件
   */
  const notifyValue = (isDelete = false) => {
    const value = values.value.join('');
    emit('update:value', value);
    emit('input', value, {
      inputType: isDelete ? 'deleteContentBackward' : 'insertText',
    } as InputResultOptions);
  };

  /**
   * 设置光标位置
   * @param pos 光标位置
   */
  const setCursorPos = (pos?: number) => {
    cursorPos.value = pos === undefined ? values.value.length : pos;
  };

  /**
   * 对输入内容进行检测，只要检查通过才会将当前输入的字符加入values中
   * @param char 当前输入的字符
   */
  const checkInput = (char: string) => {
    if (props.beforeInput) {
      const value = values.value.join('');
      const cacheChars = value.split('');
      cacheChars.splice(cursorPos.value, 0, char);
      return props.beforeInput(cacheChars.join(''), value, char);
    }
    return true;
  };

  /**
   * 插入字符
   * @param char 当前输入的字符
   * @returns void
   */
  const insertValue = (char: string) => {
    // 超过最大输入长度
    if (props.maxlength && values.value.length >= props.maxlength) {
      return;
    }
    // 输入前对字符检测
    if (!checkInput(char)) {
      return;
    }
    // 在光标前插入字符
    values.value.splice(cursorPos.value, 0, char);
    // 光标后移一位
    setCursorPos(cursorPos.value + 1);
    // 通知调用方数据变更
    notifyValue();
  };

  /** 删除已输入字符 */
  const removeValue = () => {
    const currCursorPos = cursorPos.value;
    // 光标在起始位置，没有可删除字符
    if (currCursorPos === 0) {
      return;
    }
    // 删除光标前的一位字符
    values.value.splice(currCursorPos - 1, 1);
    // 光标前移
    setCursorPos(currCursorPos - 1);
    notifyValue(true);
  };

  return {
    chars,
    cursorPos,
    values,
    initData,
    setCursorPos,
    insertValue,
    removeValue,
  };
}
